import { useState, type ReactNode } from 'react';
import type { ComplianceProvider } from '@ots-frontend/ots-data';
import {
  ArrowPathIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/20/solid';
import { AdminPage, type AdminPageProps } from '../components/AdminPage';
import { OtsActionButton } from '../components/OtsButton';
import { OtsDataTableEmpty } from '../components/OtsDataTableCells';
import { OtsInlineAlert } from '../components/OtsInlineAlert';
import { OtsMasterDetail } from '../components/OtsMasterDetail';
import { useAppDensity } from '../app/AppContext';
import { ComplianceStatusBadge } from './ComplianceStatusBadge';

export type ComplianceViewProps = {
  title?: string;
  subjectLabel?: string;
  providers: ComplianceProvider[];
  loading?: boolean;
  error?: string;
  refreshing?: boolean;
  onRefresh?: () => void;
  onOpenFullPage?: () => void;
  compact?: boolean;
  /** The dialog provides its own heading and refresh action. */
  showHeading?: boolean;
  layout?: AdminPageProps['layout'];
  renderProvider: (provider: ComplianceProvider) => ReactNode;
};

/** Agency-neutral workspace. Adding a provider does not change its navigation or page layout. */
export const ComplianceView = ({
  title = 'Compliance',
  subjectLabel,
  providers,
  loading,
  error,
  refreshing,
  onRefresh,
  onOpenFullPage,
  compact: compactProp,
  showHeading = true,
  layout = 'document',
  renderProvider,
}: ComplianceViewProps) => {
  const density = useAppDensity();
  const compact = compactProp ?? density === 'compact';
  const [selectedCode, select] = useState<string>();
  const selected =
    providers.find((provider) => provider.provider === selectedCode) ??
    providers[0];
  const fill = showHeading && layout === 'fill';
  const content = (
    <div
      className={
        fill ? 'mt-4 min-h-0 flex-1 overflow-auto' : showHeading ? 'mt-5' : ''
      }
    >
      {loading ? (
        <OtsDataTableEmpty
          compact={compact}
          loading
          loadingText="Loading compliance agencies…"
        >
          {null}
        </OtsDataTableEmpty>
      ) : error ? (
        <OtsInlineAlert
          tone="danger"
          title="Compliance is unavailable"
          size={compact ? 'compact' : 'default'}
          action={
            onRefresh && (
              <OtsActionButton size="compact" onClick={onRefresh}>
                Try again
              </OtsActionButton>
            )
          }
        >
          {error}
        </OtsInlineAlert>
      ) : !selected ? (
        <OtsDataTableEmpty compact={compact}>
          No compliance programs apply to this record.
        </OtsDataTableEmpty>
      ) : (
        <OtsMasterDetail
          navigationTitle="Agencies & programs"
          navigationLabel="Compliance programs"
          items={providers.map((provider) => ({
            id: provider.provider,
            label: provider.displayName,
            ariaLabel: provider.displayName,
            content: (
              <>
                <p className="text-xs text-[var(--ots-text-muted)]">
                  {[provider.regulator, provider.jurisdiction]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                <p className="my-1 text-sm font-semibold text-[var(--ots-secondary)]">
                  {provider.displayName}
                </p>
                {provider.subtitle && (
                  <p className="mb-2 text-xs text-[var(--ots-text-muted)]">
                    {provider.subtitle}
                  </p>
                )}
                <ComplianceStatusBadge status={provider.status} compact />
              </>
            ),
          }))}
          selectedId={selected.provider}
          onSelect={select}
          compact={compact}
          hideNavigation={providers.length === 1}
        >
          <section
            key={selected.provider}
            aria-label={selected.displayName}
            className="min-w-0 space-y-4"
          >
            {!selected.available ? (
              <OtsInlineAlert title="Program unavailable">
                {selected.reason ??
                  'This program is unavailable for this record.'}
              </OtsInlineAlert>
            ) : (
              renderProvider(selected)
            )}
          </section>
        </OtsMasterDetail>
      )}
    </div>
  );
  return (
    <div
      className={
        fill ? '@container h-full min-h-0 min-w-0' : '@container min-w-0'
      }
    >
      {showHeading ? (
        <AdminPage
          title={title}
          eyebrow={subjectLabel}
          fullWidth
          layout={layout}
          density={compact ? 'compact' : 'default'}
          subtitle="Agency requirements, processing results, and generated documents."
          actions={
            <>
              {onOpenFullPage && (
                <OtsActionButton
                  size="compact"
                  icon={<ArrowsPointingOutIcon />}
                  onClick={onOpenFullPage}
                >
                  Open full page
                </OtsActionButton>
              )}
              {onRefresh && (
                <OtsActionButton
                  size="compact"
                  icon={<ArrowPathIcon />}
                  disabled={refreshing || loading}
                  onClick={onRefresh}
                >
                  {refreshing ? 'Refreshing…' : 'Refresh'}
                </OtsActionButton>
              )}
            </>
          }
        >
          {content}
        </AdminPage>
      ) : (
        <div className={compact ? 'p-3' : 'p-5'}>{content}</div>
      )}
    </div>
  );
};
